import React, { memo } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  RadioButtons,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import { ContentCard } from "cerulean";
import { entities, modules } from "byzantine";
import {
  type WireType,
  WireTypeEnum,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";

import HeaderWithSteps from "../../../../../../../components/HeaderWithSteps";
import WireTransferRedesign from "../../../../../../../components/transfer/wires/WireTransfer";
import * as formAdapters from "../../../../../../adapters";

import { useFormContainer } from "./Form.container";
import styles from "./Form.module.scss";
import TemplateSelector from "./TemplateSelector";
import DetailsSelector from "./DetailsSelector";

type Props = {
  onSubmit: () => void;
  setWireType: (wireType: WireType) => void;
  wireType: WireType;
};

function Form({ onSubmit, setWireType, wireType }: Props) {
  const { internationalWires } = modules.wireTemplates.useWirePermissions();
  const { method, onCancel, onSubmitMethod, form, options } =
    useFormContainer(wireType);
  const limits = entities.limits.useLimits();
  const { l10n } = useLocalization();

  const radioOptions = options.reduce(
    (prev, cur) => ({ ...prev, [cur.label]: cur.id }),
    {} as Record<string, string>
  );

  const showMethodSelector = options.length > 1;

  const MethodSelector = showMethodSelector ? (
    <ContentCard kind="bordered">
      <p className={styles.wireTemplatesFormSubHeader}>
        {l10n.getString("heading-wire-method")}
      </p>
      <RadioButtons
        name="options"
        options={radioOptions}
        {...formAdapters.radio(form, "method")}
      />
    </ContentCard>
  ) : null;

  if (method === "freeform") {
    return (
      <WireTransferRedesign
        Header={
          showMethodSelector ? (
            <div className={styles.wireTemplatesMethodSelector}>
              {MethodSelector}
            </div>
          ) : undefined
        }
        limits={limits}
        setWireType={setWireType}
      />
    );
  }

  return (
    <div className={styles.sendViaWireTemplateForm}>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps headerText={l10n.getString("heading-wire-title")} />
        {MethodSelector}
        <TemplateSelector />
        <DetailsSelector />
        <Row alignItems="center">
          <Row.Item>
            <Button
              label={l10n.getString("button-back")}
              kind="negative"
              onClick={
                internationalWires
                  ? () => setWireType(WireTypeEnum.None)
                  : onCancel
              }
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={!method ? onSubmitMethod : onSubmit}
            />
          </Row.Item>
        </Row>
      </ResponsiveFlex>
    </div>
  );
}

export default memo(Form);
